/* @flow */
/** @module util/redux */

// Type defs //////////////////////////////////////////////////////////////////

export type SelectorProps = Object

export type Action = {|
  // See: https://github.com/acdlite/flux-standard-action#actions
  +type: string,
  +payload?: any,
  +error?: boolean
|}

export type ActionWithMeta = {|
  +type: string,
  +payload?: any,
  +meta?: any,
  +error?: boolean
|}

export type Dispatch = (
  action: Action | ActionWithMeta | ThunkAction | PromiseAction
) => any
export type GetState = () => Object
export type ThunkAction = (dispatch: Dispatch, getState: GetState) => any
export type ThunkPromise<Payload> = Promise<{
  +type: string,
  +payload: Payload,
  +error?: Object
}>
export type PromiseAction = Promise<Action>

// Store //////////////////////////////////////////////////////////////////////

/**
 * Get item from store; defaults to empty object
 * @param  {object} store - Redux store
 * @param  {string} id - Item id
 * @return {object} - Item in store or empty object
 */
export const getItemSafe = (store: Object, id: string): Object =>
  store[id] || {}

/**
 * Get item's META from store; defaults to empty object
 * @param  {object} store - Redux store
 * @param  {string} id - Item id
 * @return {object} - Item's META or empty object
 */
export const getItemMetaSafe = (store: Object, id: string): Object =>
  getItemSafe(store, id).META || {}

/* eslint-disable max-len */
/**
 * Watch store for change
 * @function
 * @param {object} store - Redux Store
 * @param {function} select - Redux selector function
 * @param {function} onChange - Function fire when the state changes
 * @returns {function} - Function that, when called, unsubscribes from store changes
 */
export const observeStore = (
  store: { getState: GetState, subscribe: (() => void) => any },
  select: Object => any,
  onChange: (Object, Object) => any
) => {
  /* eslint-enable max-len */
  let currentState = {}

  const handleChange = () => {
    const priorState = currentState
    currentState = select(store.getState())

    if (priorState !== currentState) {
      onChange(currentState, priorState)
    }
  }

  const unsubscribe = store.subscribe(handleChange)
  handleChange()
  return unsubscribe
}
// // function observeStore(store, select, onChange) {
//   let currentState
//
//   const handleChange = () => {
//     const nextState = select(store.getState())
//     if(nextState !== currentState){
//       currentState = nextState
//       onChange(currentState)
//     }
//   }
//
//   const unsubscribe = store.subscribe(handleChange)
//   handleChange()
//   return unsubscribe
// }

// Actions ////////////////////////////////////////////////////////////////////

/**
 * @typedef {object} ActionMetaObject
 * @property {number} actionCreatedAt
 */

/**
 * Create default action metadata
 * @return {ActionMetaObject} - Default action metadata
 */
export const createActionMeta = () => ({
  actionCreatedAt: Date.now().valueOf()
})

/**
 * @summary Standard Redux store `SET` functionality
 * @description Handles payloads that are a single item or an array of items; preserves `META`
 * @param  {object} state - App state/store
 * @param  {object} action - dispatched action
 * @return {object} - New copy of `state`
 */
export const standardSet = (
  state: Object,
  action: { payload: Array<Object> | Object }
) => {
  if (Array.isArray(action.payload)) {
    return {
      ...state,
      ...action.payload.reduce(
        (newModels, model) => ({
          ...newModels,
          [model.id]: {
            META: (state[model.id] || {}).META || {},
            ...model
          }
        }),
        {}
      )
    }
  } else {
    return {
      ...state,
      [action.payload.id]: {
        META: (state[action.payload.id] || {}).META || {},
        ...action.payload
      }
    }
  }
}

type METASetPayload = {|
  id: string,
  META: Object
|}

/**
 * @summary Standard Redux store `SET` functionality for META attributes
 * @description Handles payloads that are a single item or an array of items; preserves `META`
 * @param  {object} state - App state/store
 * @param  {object} action - dispatched action
 * @return {object} - New copy of `state`
 */
export const standardMETASet = (
  state: Object,
  action: { payload: Array<METASetPayload> | METASetPayload }
) => {
  if (Array.isArray(action.payload)) {
    return {
      ...state,
      ...action.payload.reduce(
        (newModels, model: METASetPayload) => ({
          ...newModels,
          [model.id]: {
            ...getItemSafe(state, model.id),
            META: { ...getItemMetaSafe(state, model.id), ...model.META }
          }
        }),
        {}
      )
    }
  } else {
    return {
      ...state,
      [action.payload.id]: {
        ...(state[action.payload.id] || {}),
        META: {
          ...((state[action.payload.id] || {}).META || {}),
          ...action.payload.META
        }
      }
    }
  }
}

/**
 * ensures a reducer spread returns a safe {} if the data lookup procedure
 * returns anything falsy
 * @param {Object} entity - the value with which to start the lookup process
 * @param {string[]} lookup - an array of strings representing the data address
 * @example ...safe(state[action.payload.location.id], 'META', '),
 * @returns {*|{}} - either an empty Object or some data
 */
export const safe = (entity: Object, lookup: Array<string>): mixed | {} => {
  if (!entity) return {}

  let acc = entity
  const isSafe = lookup.every(arg => {
    acc = acc[arg]
    return Boolean(acc[arg])
  })

  return isSafe ? acc : {}
}

/**
 * use in componentWillUpdate to determine when a piece of data's META.requestStatus prop changes to 'REQUEST_SUCCESSFUL', indicating
 * data load is complete
 * @param {Object} nextProp - the data point among nextProps to check
 * @param {Object} prop - the data point among props to check against
 * @returns {bool} whether this event has just occurred
 */
export const triggerOnRequestStatusSuccess = (
  nextProp: Object,
  prop: Object
) => {
  if (!nextProp || !prop) {
    return false
  }

  const { META: { requestStatus } = {} } = prop
  const { META: { requestStatus: nextRequestStatus } = {} } = nextProp

  return (
    nextRequestStatus !== requestStatus &&
    nextRequestStatus === 'REQUEST_SUCCESSFUL'
  )
}
